import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from "@-/com.npmjs.react-native";
import { useRouteBodyRef } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { NavigationContext } from "@-/com.npmjs.expo-router/react-navigation";
import { useCallback, useContext, useState } from "react";

/** Scroll restoration waits for progressive Stats content and yields immediately to a drag. */
export function useStatsScrollRestoration(
  key: string,
  {
    horizontal = false,
    initiallyAtEnd = false,
  }: {
    horizontal?: boolean;
    initiallyAtEnd?: boolean;
  } = {},
) {
  const navigation = useContext(NavigationContext);
  const saved = useRouteBodyRef<number | null>(key, null);
  const [initialContentOffset] = useState(() => ({
    x: horizontal ? (saved.current ?? 0) : 0,
    y: horizontal ? 0 : (saved.current ?? 0),
  }));
  const [restoration] = useState(() => ({
    contentExtent: 0,
    hasViewportLayout: false,
    initialEnd: initiallyAtEnd && saved.current === null,
    lastAttempt: "",
    node: null as ScrollView | null,
    pending: saved.current !== null || initiallyAtEnd,
    target: saved.current ?? 0,
    trailingInset: 0,
    userDragged: false,
    viewportExtent: 0,
  }));
  const restore = useCallback(() => {
    if (
      !restoration.pending ||
      restoration.node === null ||
      restoration.viewportExtent <= 0 ||
      navigation?.isFocused() === false
    ) {
      return;
    }
    const maximum = Math.max(
      0,
      restoration.contentExtent - restoration.viewportExtent + restoration.trailingInset,
    );
    const target = restoration.initialEnd ? maximum : restoration.target;
    // Never accept a loading placeholder's clamp as the saved position. A shorter final page
    // stays naturally clamped until more data arrives or the user takes control with a drag.
    if (restoration.contentExtent <= 0 || target > maximum + 1) {
      return;
    }
    const attempt = `${target}:${restoration.contentExtent}:${restoration.viewportExtent}`;
    if (restoration.lastAttempt === attempt) {
      return;
    }
    restoration.lastAttempt = attempt;
    restoration.node.scrollTo({
      animated: false,
      x: horizontal ? target : 0,
      y: horizontal ? 0 : target,
    });
  }, [horizontal, navigation, restoration]);
  const scrollRef = useCallback(
    (node: ScrollView | null) => {
      restoration.node = node;
      restoration.lastAttempt = "";
      restore();
    },
    [restoration, restore],
  );
  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      restoration.hasViewportLayout = true;
      restoration.viewportExtent = horizontal
        ? event.nativeEvent.layout.width
        : event.nativeEvent.layout.height;
      restore();
    },
    [horizontal, restoration, restore],
  );
  const onContentSizeChange = useCallback(
    (width: number, height: number) => {
      restoration.contentExtent = horizontal ? width : height;
      restore();
    },
    [horizontal, restoration, restore],
  );
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      // Native screen detachment can emit its clamp during navigation blur, before React has
      // removed this body and nulled the ref. Read focus at event time: the last render's focus
      // value is still true in that interval. Neither that event nor a detached host owns the
      // route's surviving scalar state anymore.
      if (restoration.node === null || navigation?.isFocused() === false) {
        return;
      }
      const { contentInset, contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
      restoration.contentExtent = horizontal ? contentSize.width : contentSize.height;
      // A queued native scroll can carry the previous viewport after the current onLayout.
      // Overwriting that layout can make a valid saved bottom offset appear unreachable forever.
      // Scroll metrics are only a fallback until layout arrives; later resizes use onLayout too.
      if (!restoration.hasViewportLayout) {
        restoration.viewportExtent = horizontal
          ? layoutMeasurement.width
          : layoutMeasurement.height;
      }
      restoration.trailingInset = (horizontal ? contentInset?.right : contentInset?.bottom) ?? 0;
      const maximum = Math.max(
        0,
        restoration.contentExtent - restoration.viewportExtent + restoration.trailingInset,
      );
      const offset = Math.max(0, horizontal ? contentOffset.x : contentOffset.y);
      if (
        !restoration.userDragged &&
        !restoration.pending &&
        saved.current !== null &&
        saved.current > maximum + 1
      ) {
        restoration.pending = true;
        restoration.target = saved.current;
        restoration.lastAttempt = "";
      }
      if (restoration.pending) {
        const target = restoration.initialEnd ? maximum : restoration.target;
        if (
          restoration.contentExtent <= 0 ||
          restoration.viewportExtent <= 0 ||
          Math.abs(offset - target) > 1
        ) {
          restore();
          return;
        }
        restoration.pending = false;
        restoration.initialEnd = false;
      }
      saved.current = offset;
    },
    [horizontal, navigation, restoration, restore, saved],
  );
  const onScrollBeginDrag = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (restoration.node === null || navigation?.isFocused() === false) {
        return;
      }
      restoration.userDragged = true;
      restoration.pending = false;
      restoration.initialEnd = false;
      saved.current = Math.max(
        0,
        horizontal ? event.nativeEvent.contentOffset.x : event.nativeEvent.contentOffset.y,
      );
    },
    [horizontal, navigation, restoration, saved],
  );
  return {
    initialContentOffset,
    onContentSizeChange,
    onLayout,
    onScroll,
    onScrollBeginDrag,
    scrollRef,
  };
}
